import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { AlertCircle, QrCode, CheckCircle, XCircle } from 'lucide-react';
import Button from '../ui/Button';
import Card from '../ui/Card.jsx';
import Input from '../ui/Input.jsx';

const ValidationSection = ({
  validationCode,
  setValidationCode,
  handleValidateCode,
  onOpenScanner,
  validationError,
  session
}) => {
  const [isValidating, setIsValidating] = useState(false);
  const [scanSuccess, setScanSuccess] = useState(false);

  const validateCode = async () => {
    if (!validationCode.trim()) {
      return;
    }

    setIsValidating(true);
    try {
      await handleValidateCode(validationCode);
      setScanSuccess(true);
      setTimeout(() => {
        setScanSuccess(false);
      }, 2000);
    } catch (error) {
      console.error('Validation error:', error);
    } finally {
      setIsValidating(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      validateCode();
    }
  };

  return (
    // Karte ohne eigenes Innenmaß: Vorher brachte `SharedStyles.card` bereits
    // p-3/p-4 mit und der Inhalt legte p-3/p-6 obendrauf — doppelte Polsterung,
    // und der Trennstrich zum Infoblock endete vor dem Kartenrand.
    <Card className="overflow-hidden">
      <div className="space-y-4 p-4 sm:p-5">
        {/* Header */}
        <div className="flex items-start gap-3">
          <div className="shrink-0 rounded-lg bg-warning-soft p-2">
            <AlertCircle className="h-5 w-5 text-warning" aria-hidden="true" />
          </div>
          <div className="min-w-0">
            <h4 className="text-sm sm:text-base font-medium text-foreground">
              Session-Validierung erforderlich
            </h4>
            <p className="mt-1 text-xs text-muted-foreground">
              Bitte gib den Session-Code ein oder scanne den QR-Code, um an der Session „{session.topic}" teilzunehmen.
            </p>
          </div>
        </div>

        {/* Input Section */}
        <div className="space-y-3">
          <div className="flex flex-col gap-2 sm:flex-row">
            <div className="relative flex-1">
              <Input
                type="text"
                value={validationCode}
                onChange={(e) => setValidationCode(e.target.value.toUpperCase())}
                onKeyDown={handleKeyDown}
                // Der Code ist eine 6-stellige Zahl — Buchstaben im Beispiel
                // und die Buchstabentastatur fuehrten auf die falsche Faehrte.
                placeholder="z.B. 123456"
                inputMode="numeric"
                aria-label="Session-Code eingeben"
                error={Boolean(validationError)}
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                enterKeyHint="go"
                maxLength={6}
                className={`pr-11 font-mono text-lg tracking-wider ${scanSuccess ? 'border-success' : ''}`}
              />
              {(validationError || scanSuccess) && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  {validationError ? (
                    <XCircle className="h-5 w-5 text-danger" aria-hidden="true" />
                  ) : (
                    <CheckCircle className="h-5 w-5 text-success" aria-hidden="true" />
                  )}
                </div>
              )}
            </div>
            <Button variant="secondary" onClick={onOpenScanner} className="shrink-0 whitespace-nowrap">
              <QrCode className="h-5 w-5" aria-hidden="true" />
              QR-Code scannen
            </Button>
          </div>

          {validationError && (
            <div className="flex items-center gap-2 rounded-xl border border-danger/40 bg-danger-soft p-3 text-foreground" role="alert">
              <AlertCircle className="h-5 w-5 shrink-0 text-danger" aria-hidden="true" />
              <span className="text-sm">{validationError}</span>
            </div>
          )}

          {scanSuccess && (
            <div className="flex items-center gap-2 rounded-xl border border-success/40 bg-success-soft p-3 text-foreground" role="status">
              <CheckCircle className="h-5 w-5 shrink-0 text-success" aria-hidden="true" />
              <span className="text-sm">Code erfolgreich validiert!</span>
            </div>
          )}
        </div>

        {/* Validate Button */}
        <Button
          variant="primary"
          onClick={validateCode}
          disabled={!validationCode.trim() || isValidating}
          fullWidth
        >
          {isValidating ? 'Validiere...' : 'Code validieren'}
        </Button>
      </div>

      {/* Info Section */}
      <div className="border-t border-border bg-surface-raised p-3 sm:p-4">
        <div className="flex items-start gap-2">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          <p className="text-xs text-muted-foreground">
            Der Session-Code wurde vom Host geteilt. Falls du ihn nicht hast, frage bitte den Host nach dem Code oder scanne den QR-Code.
          </p>
        </div>
      </div>
    </Card>
  );
};

ValidationSection.propTypes = {
  validationCode: PropTypes.string.isRequired,
  setValidationCode: PropTypes.func.isRequired,
  handleValidateCode: PropTypes.func.isRequired,
  onOpenScanner: PropTypes.func.isRequired,
  validationError: PropTypes.string,
  session: PropTypes.shape({
    topic: PropTypes.string.isRequired
  }).isRequired
};

export default ValidationSection;
